import { Link, useLocation } from 'react-router-dom';
import { resourceArticlePath } from '../workspace/clientCarePaths';
import type { PsychoeducationArticlePreview } from '@lighthouse/shared';
import { resolveLearnArticleImageUrl } from '@lighthouse/shared';
import { estimateReadMinutes, formatArticleDate } from './learnArticleUtils';

interface LearnArticleCardProps {
  article: PsychoeducationArticlePreview;
  expanded: boolean;
  onToggle: () => void;
}

export function LearnArticleCard({ article, expanded, onToggle }: LearnArticleCardProps) {
  const { pathname } = useLocation();
  const imageUrl = resolveLearnArticleImageUrl(article.slug, article.imageUrl);
  const readMinutes = estimateReadMinutes(article);
  const publishedLabel = formatArticleDate(article.publishedAt);

  return (
    <article className="learn-article-card">
      <div className="learn-article-card-media">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={article.title}
            className="learn-article-card-image"
            loading="lazy"
          />
        ) : (
          <div className="learn-article-card-placeholder" aria-hidden>
            <span>🧠</span>
          </div>
        )}
        <span className="learn-article-card-category">{article.category}</span>
      </div>

      <div className="learn-article-card-body">
        <button
          type="button"
          className="learn-article-card-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <h3 className="learn-article-card-title">{article.title}</h3>
          <p className="learn-article-card-summary">{article.summary}</p>
          <span className="learn-article-card-more">
            {expanded ? 'Show less' : 'Preview excerpt'}
            <ChevronIcon expanded={expanded} />
          </span>
        </button>

        <p className="learn-article-card-meta">
          {article.authorName}
          {publishedLabel ? ` · ${publishedLabel}` : ''}
          <span className="learn-article-card-meta-dot" aria-hidden>
            ·
          </span>
          {readMinutes} min read
        </p>

        <div className={`learn-article-card-expand ${expanded ? 'is-open' : ''}`}>
          <div className="learn-article-card-expand-inner">
            {article.expandParagraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 48)} className="learn-article-card-excerpt">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <Link to={resourceArticlePath(pathname, article.slug)} className="learn-article-card-cta">
          Read full article
          <span aria-hidden>→</span>
        </Link>
      </div>
    </article>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`learn-article-card-chevron ${expanded ? 'is-open' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export function LearnArticleCardSkeleton() {
  return (
    <div className="learn-article-card learn-article-card--skeleton" aria-hidden>
      <div className="learn-article-card-media learn-article-card-media--skeleton" />
      <div className="learn-article-card-body space-y-3 p-5">
        <div className="skeleton h-3 w-20" />
        <div className="skeleton h-5 w-full" />
        <div className="skeleton h-4 w-full" />
        <div className="skeleton h-4 w-5/6" />
        <div className="skeleton mt-2 h-3 w-32" />
      </div>
    </div>
  );
}
